import type { ShotInput, SportsGameSettings } from '../../sports.types';
import type {
  ISportDefinition,
  SportAssets,
  SportPhysicsParams,
  StartingPosition,
  ScoringZone,
  ShotEvaluation,
} from '../sport-definition.interface';
import { evaluateGoalShot } from '../zone-scoring.utils';

// ─── Physics constants ─────────────────────────────────────────────────────────

const PHYSICS: SportPhysicsParams = {
  ballRadius: 0.038,            // NHL puck ≈ 7.6 cm diameter, 2.5 cm tall
  launchSpeedScale: 6.5,        // Puck travels fast
  arcHeightFactor: 0.4,         // Low arc — puck stays below crossbar, skill trap for high shots
  assistCorrectionStrength: 0.55,
  goalToleranceRadius: 0.70,    // Hockey net is wide: 1.83 m → ~0.70 scene units half-width
  optimalElevationDeg: 20,      // 0° = along ice, 20° = top-shelf attempt angle
  shankBaseThreshold: 0.85,
  shankDriftScale: 1.8,
};

/**
 * Key design detail: arcHeightFactor = 0.20 means a high-elevation swipe sends
 * the puck OVER the net (net height ≈ 0.45 scene units). This is an intentional
 * skill trap — greedy players who swipe too steeply miss high.
 */
const GOAL_HEIGHT = 0.45; // NHL net: 1.22 m → ~0.45 scene units

// ─── Scoring zones ─────────────────────────────────────────────────────────────

/**
 * Net face: landscape orientation. 1.4 wide × 0.45 tall in scene units.
 * Top corners are the hardest for a goalie to cover → reward 3 pts.
 * Low center is the easiest save → only 1 pt.
 *
 * X: -0.70 (left post) → +0.70 (right post)
 * Y: 0.00 (ice surface) → 0.45 (crossbar)
 */
const SCORING_ZONES: ScoringZone[] = [
  {
    id: 'top-left',
    points: 3,
    bounds: { xMin: -0.70, xMax: -0.25, yMin: 0.25, yMax: 0.45 },
  },
  {
    id: 'top-right',
    points: 3,
    bounds: { xMin: 0.25, xMax: 0.70, yMin: 0.25, yMax: 0.45 },
  },
  {
    id: 'top-center',
    points: 2,
    bounds: { xMin: -0.25, xMax: 0.25, yMin: 0.25, yMax: 0.45 },
  },
  {
    id: 'low-side',
    points: 2,
    bounds: { xMin: -0.70, xMax: -0.25, yMin: 0.00, yMax: 0.25 },
  },
  {
    id: 'low-side-right',
    points: 2,
    bounds: { xMin: 0.25, xMax: 0.70, yMin: 0.00, yMax: 0.25 },
  },
  {
    id: 'low-center',
    points: 1,
    bounds: { xMin: -0.25, xMax: 0.25, yMin: 0.00, yMax: 0.25 },
  },
];

// ─── Starting positions ────────────────────────────────────────────────────────

/**
 * 7 positions ordered from easiest (slot center, closest/straightest) to
 * hardest (left/right point, deep angle + long distance).
 *
 * getStartingPositions(attempts) takes the first N entries, so operators with
 * fewer attempts automatically get the easiest subset.
 */
const ALL_POSITIONS: StartingPosition[] = [
  { lateralOffset:  0.0, distance: 1.5 }, // 1. slot center — best scoring chance
  { lateralOffset: +0.9, distance: 2.0 }, // 2. right circle
  { lateralOffset: -0.9, distance: 2.0 }, // 3. left circle
  { lateralOffset:  0.0, distance: 2.5 }, // 4. high slot
  { lateralOffset:  0.0, distance: 3.0 }, // 5. top of circle
  { lateralOffset: +1.4, distance: 3.2 }, // 6. right point
  { lateralOffset: -1.4, distance: 3.2 }, // 7. left point
];

// ─── Assets ────────────────────────────────────────────────────────────────────

const ASSETS: SportAssets = {
  // Phase 3+: ballModelPath: 'assets/sports/ice-hockey/puck.glb'
  ballColor: '#1a1a1a', // black puck
};

// ─── Sport definition ──────────────────────────────────────────────────────────

export class IceHockeySportDefinition implements ISportDefinition {
  readonly id = 'ice-hockey' as const;
  readonly displayName = 'Slap Shot';
  readonly scoringModel = 'zone' as const;
  readonly assets = ASSETS;
  readonly physicsParams = PHYSICS;
  readonly startingPositions = ALL_POSITIONS;
  readonly scoringZones = SCORING_ZONES;
  readonly maxAttempts = 7;

  readonly arCameraHint = { pitch: -8, yaw: 0 };
  readonly arSceneRotation: [number, number, number] = [-Math.PI / 2, 0, 0];

  /**
   * Returns the first N positions from ALL_POSITIONS (easiest-first ordering).
   * Operators with fewer attempts automatically play only the easier positions.
   *
   * | n | Positions used                               |
   * |---|----------------------------------------------|
   * | 1 | slot center only                             |
   * | 2 | slot + right circle                          |
   * | 3 | slot + both circles                          |
   * | 4 | slot + circles + high slot                   |
   * | 5 | above + top of circle                        |
   * | 6 | above + right point                          |
   * | 7 | all 7                                        |
   */
  getStartingPositions(attempts: number): StartingPosition[] {
    return ALL_POSITIONS.slice(0, Math.min(Math.max(1, attempts), ALL_POSITIONS.length));
  }

  evaluateShot(
    input: ShotInput,
    assist: number,
    settings: SportsGameSettings
  ): ShotEvaluation {
    const {
      launchSpeedScale,
      optimalElevationDeg = 20,
      goalToleranceRadius,
      assistCorrectionStrength,
      shankBaseThreshold = 0.85,
      shankDriftScale = 1.8,
    } = this.physicsParams;

    // ── Whiff check ──────────────────────────────────────────────────────────
    if (input.overSwing) {
      return evaluateGoalShot({
        finalX: 0, finalY: 0, finalZ: 0,
        goalToleranceRadius, goalHeight: GOAL_HEIGHT,
        zones: SCORING_ZONES, pointsPerSuccess: settings.pointsPerSuccess,
        isWhiff: true,
      });
    }

    // ── Trajectory ───────────────────────────────────────────────────────────
    const launchAngleDeg = input.direction.y * optimalElevationDeg * 2;
    const launchAngleRad = (launchAngleDeg * Math.PI) / 180;
    const sinAngle = Math.sin(launchAngleRad);
    const cosAngle = Math.cos(launchAngleRad);

    // Vertical: puck rises proportionally to elevation angle.
    // arcHeightFactor=0.20 keeps it low — swipe too steeply → flies over net.
    const finalY = Math.max(
      0,
      input.power * launchSpeedScale * sinAngle * 0.25
    );

    // Lateral: aim + assist correction + shank
    const rawLateral = input.direction.x;
    const correctedLateral = rawLateral * (1 - assist * assistCorrectionStrength);
    const shankThreshold = shankBaseThreshold + assist * 0.1;
    const shankDrift =
      input.power > shankThreshold
        ? (input.power - shankThreshold) * shankDriftScale * (Math.random() > 0.5 ? 1 : -1)
        : 0;
    const finalX = correctedLateral * goalToleranceRadius * 1.4 + shankDrift;

    return evaluateGoalShot({
      finalX,
      finalY,
      finalZ: -input.power * launchSpeedScale * cosAngle,
      goalToleranceRadius,
      goalHeight: GOAL_HEIGHT,
      zones: SCORING_ZONES,
      pointsPerSuccess: settings.pointsPerSuccess,
      isWhiff: false,
    });
  }
}
